import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, Loader2, ArrowRight, AlertCircle, Sparkles, Flame, TrendingUp, Moon, Heart, X } from 'lucide-react';
import { toast } from 'sonner';
import { healthApi } from '@/api/healthApi';
import { useAnimatedCounter } from '@/hooks/useAnimatedCounter';
import { checkinQueue } from '@/services/wellnessQueues';
import { SyncStatusPill } from '@/components/SyncStatusPill';
import { isOnline } from '@/services/localFirstQueue';

/**
 * Live snapshot card shown above static patterns on the post-submit insight
 * screen — so the user immediately sees their freshly updated score, streak
 * and completion %, with the score animating from 0 to its new value.
 */
const TodaySnapshot = ({ snapshot }) => {
  const { t } = useLanguage();
  const score = snapshot?.health_score ?? 0;
  const animatedScore = useAnimatedCounter(score, 800);
  const completion = snapshot?.completion_percentage ?? 0;
  const streak = snapshot?.current_streak ?? 0;
  const tasksDone = Math.round((completion / 100) * 4);

  return (
    <Card
      className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white"
      data-testid="checkin-snapshot-card"
    >
      <CardContent className="p-6">
        <p className="text-xs font-semibold text-emerald-50/90 uppercase tracking-widest mb-3">
          {t('checkin.snapshot.title')}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-2xl font-bold leading-none" data-testid="checkin-snapshot-score">
              {animatedScore}
            </p>
            <p className="text-[11px] text-emerald-50/85 mt-1">{t('checkin.snapshot.healthScore')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold leading-none" data-testid="checkin-snapshot-tasks">
              {tasksDone}/4
            </p>
            <p className="text-[11px] text-emerald-50/85 mt-1">{t('checkin.snapshot.todayTasks')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold leading-none flex items-center justify-center gap-1" data-testid="checkin-snapshot-streak">
              <Flame className="h-5 w-5" />
              {streak}
            </p>
            <p className="text-[11px] text-emerald-50/85 mt-1">{t('checkin.snapshot.dayStreak')}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const InsightsScreen = ({ insights, snapshot, aiInsight, onBack, onNew }) => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 overflow-y-auto pb-32" data-testid="checkin-insights-screen">
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">

        {/* Header */}
        <div className="text-center mb-2">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {t('checkin.insights.title')}
          </h1>
          <p className="text-sm text-slate-600">
            {t('checkin.insights.subtitle')}
          </p>
        </div>

        {/* Live snapshot of the freshly updated dashboard */}
        {snapshot && <TodaySnapshot snapshot={snapshot} />}

        {/* Behaviour-aware AI insight from backend (priority over the static opening) */}
        {aiInsight ? (
          <Card
            className="border-0 shadow-lg rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 relative overflow-hidden"
            data-testid="checkin-ai-insight-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">{t('checkin.insights.insightForYou')}</h3>
              </div>
              <p className="text-base font-semibold text-gray-900 mb-2">
                {aiInsight.title}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {aiInsight.message}
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Fallback opening when the backend insight isn't available */
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-6">
              <p className="text-base text-slate-700 leading-relaxed">
                {insights.opening}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Static rule-based patterns from the local engine */}
        {insights.patterns.map((pattern) => (
          <Card key={pattern} className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                </div>
                <p className="text-sm text-blue-900 leading-relaxed font-medium">
                  {pattern}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Actions */}
        <Card className="border-0 shadow-lg rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.insights.simpleActions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {insights.actions.map((action) => (
              <div
                key={action}
                className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200"
              >
                <ArrowRight className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-emerald-900 font-medium">{action}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* When to Seek Help */}
        <Card className="border-0 shadow-md rounded-2xl bg-orange-50 border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <h3 className="text-sm font-bold text-orange-900 mb-3">{t('checkin.insights.whenToSeekHelp')}</h3>
            <ul className="space-y-2">
              {insights.seekHelp.map((item) => (
                <li key={item} className="text-sm text-orange-800 flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-orange-900 font-semibold mt-4">
              {t('checkin.insights.nhsAdvice')}
            </p>
          </CardContent>
        </Card>

        {/* CTAs */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={onBack}
            variant="outline"
            className="flex-1 h-12 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="checkin-back-to-dashboard-btn"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            {t('checkin.insights.backToDashboard')}
          </Button>
          <Button
            onClick={onNew}
            className="flex-1 h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl"
            data-testid="checkin-new-checkin-btn"
          >
            {t('checkin.insights.newCheckIn')}
          </Button>
        </div>

        <div className="pt-4">
          <p className="text-xs text-center text-slate-400">
            {t('checkin.insights.disclaimer')}
          </p>
        </div>

      </div>
    </div>
  );
};

const SmartDailyCheckIn = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Section 1: How are you feeling
  const [feelings, setFeelings] = useState([]);
  
  // Section 2: Your basics
  const [sleep, setSleep] = useState(null);
  // Sleep quality enrichment (added 2026-05-25): captures a 4-level felt
  // sleep quality + an optional sleep duration range. `sleep` (yes/no) is
  // kept in sync below so existing AI insight / task-completion logic
  // (sleep === 'yes' | 'no') continues to work unchanged.
  const [sleepQuality, setSleepQuality] = useState(null);
  const [sleepDuration, setSleepDuration] = useState(null);
  const [water, setWater] = useState(null);
  // Hydration enrichment (added 2026-05-24): captures a 4-level felt
  // hydration scale + an optional water intake range. `water` (yes/no)
  // is kept in sync below so existing AI insight / task-completion logic
  // continues to work unchanged.
  const [hydrationLevel, setHydrationLevel] = useState(null);
  const [waterIntake, setWaterIntake] = useState(null);
  const [food, setFood] = useState(null);
  // Nutrition habits (added 2026-05-25): 3-level meal balance + optional
  // fruit & veg servings tracker. Legacy `food` (yes/no) auto-synced
  // below so existing AI insight + completion-task logic continues to
  // work unchanged. Non-diagnostic, encouraging tone.
  const [mealBalance, setMealBalance] = useState(null);
  const [fruitVegServings, setFruitVegServings] = useState(null);
  const [activity, setActivity] = useState(null);
  // Activity enrichment (added 2026-05-25): captures a 4-level felt
  // activity scale + an optional multi-select list of activity types.
  // `activity` (legacy 3-bucket) is kept in sync below so existing AI
  // insight + completion-task logic continues to work unchanged.
  const [activityLevel, setActivityLevel] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  // Caffeine intake (added 2026-05-25): lightweight single-select 4-bucket
  // tracker. Educational nudge varies based on intake — gentle reminder
  // about sleep/hydration balance for higher intakes. Strictly additive,
  // non-diagnostic.
  const [caffeineIntake, setCaffeineIntake] = useState(null);
  // Sugary drinks (added 2026-05-25): lightweight single-select 4-bucket
  // tracker for fizzy / sugar-sweetened beverages. Educational nudge
  // varies based on intake — preventive-wellness reminder about hydration,
  // energy crashes, and sugar pacing for higher intakes. Non-diagnostic.
  const [sugaryDrinks, setSugaryDrinks] = useState(null);
  // Medication & pain relief check-in (added 2026-05-25): educational only,
  // never directive. Tracks whether the user took medication today across
  // 4 buckets, plus an optional free-text note. The nudge becomes more
  // explicit about partnering with a healthcare professional when any
  // "Yes" option is selected — no diagnosis, no treatment advice.
  const [medicationToday, setMedicationToday] = useState(null);
  const [medicationNote, setMedicationNote] = useState('');
  // Emotional wellness section (added 2026-05-25): multi-select emotion
  // chips + an optional free-text note. Strictly additive — does not
  // interfere with the existing physical-feelings section (Section 1) or
  // the stress/mood/energy mind section. Tone is supportive, non-diagnostic.
  const [emotions, setEmotions] = useState([]);
  const [emotionsNote, setEmotionsNote] = useState('');
  
  // Section 3: Mind & energy
  const [stress, setStress] = useState(null);
  // Stress check-in enrichment (added 2026-05-25): 5-level felt scale +
  // optional multi-select coping activities. Legacy `stress` (low/
  // moderate/high) auto-synced below so the existing AI insight rules
  // (e.g. stress === 'high' && exposures.includes('noise')) and the
  // mind-section completion gating continue to work unchanged. Calm,
  // non-clinical tone.
  const [stressLevel, setStressLevel] = useState(null);
  const [copingActivities, setCopingActivities] = useState([]);
  // Lifestyle habits today — multi-select chips with mutually-exclusive
  // "None of the above". Tracks common daily lifestyle inputs (smoking,
  // vaping, alcohol, fast food, energy drinks) that may correlate with
  // sleep / energy / mood patterns over time. Strictly observational —
  // never used to generate diagnostic conclusions or risk scores.
  const [lifestyleHabits, setLifestyleHabits] = useState([]);
  // General wellness symptoms (added 2026-05-25): multi-select chip
  // tracker. Never diagnostic; the supportive nudge encourages
  // partnering with a healthcare professional when persistent / severe
  // symptoms are present.
  const [symptoms, setSymptoms] = useState([]);
  // Energy level enrichment (added 2026-05-25): 4-level felt scale
  // replacing the legacy low/okay/high. Legacy `energy` flag auto-synced
  // below so existing AI insight rules (energy === 'low') and the
  // mind-section completion gating keep working.
  const [energyLevel, setEnergyLevel] = useState(null);
  const [mood, setMood] = useState(null);
  const [energy, setEnergy] = useState(null);
  
  // Section 4: Your day
  const [routine, setRoutine] = useState(null);
  
  // Section 5: Your environment
  const [environment, setEnvironment] = useState(null);
  const [environmentAffected, setEnvironmentAffected] = useState(null);
  const [exposures, setExposures] = useState([]);

  // Hydration option constants — kept near the top so the JSX block stays
  // declarative. Values are short tokens we can persist + analyse later
  // without locking the UI copy to backend strings.
  const HYDRATION_OPTIONS = [
    { value: 'very_hydrated',       label: 'Very hydrated' },
    { value: 'moderately',          label: 'Moderately hydrated' },
    { value: 'slightly_dehydrated', label: 'Slightly dehydrated' },
    { value: 'very_dehydrated',     label: 'Very dehydrated' },
  ];
  const WATER_INTAKE_OPTIONS = [
    { value: 'lt1',   label: 'Less than 1L' },
    { value: '1_2',   label: '1–2L' },
    { value: '2_3',   label: '2–3L' },
    { value: '3plus', label: '3L+' },
  ];
  // Sleep option constants — mirror the hydration pattern so the basics
  // section stays visually consistent (2x2 quality grid + optional 4-bucket
  // duration row). Tokens are stable; UI copy comes through i18n.
  const SLEEP_QUALITY_OPTIONS = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'good',      label: 'Good' },
    { value: 'fair',      label: 'Fair' },
    { value: 'poor',      label: 'Poor' },
  ];
  const SLEEP_DURATION_OPTIONS = [
    { value: 'lt4',   label: 'Less than 4 hours' },
    { value: '4_6',   label: '4–6 hours' },
    { value: '6_8',   label: '6–8 hours' },
    { value: '8plus', label: '8+ hours' },
  ];
  // Physical activity options — 4-level felt scale + 6 multi-select types.
  const ACTIVITY_LEVEL_OPTIONS = [
    { value: 'very_active',     label: 'Very active' },
    { value: 'moderately',      label: 'Moderately active' },
    { value: 'lightly',         label: 'Lightly active' },
    { value: 'sedentary',       label: 'Mostly sedentary' },
  ];
  const ACTIVITY_TYPE_OPTIONS = [
    { value: 'walking',     label: 'Walking' },
    { value: 'gym',         label: 'Gym' },
    { value: 'stretching',  label: 'Stretching' },
    { value: 'sports',      label: 'Sports' },
    { value: 'house_chores',label: 'House chores' },
    { value: 'other',       label: 'Other' },
  ];
  // Caffeine intake — single-select 4 buckets. Amber palette for visual
  // coffee/tea cue without veering into warning colors.
  const CAFFEINE_OPTIONS = [
    { value: 'none',  label: 'None' },
    { value: '1',     label: '1 cup' },
    { value: '2_3',   label: '2–3 cups' },
    { value: '4plus', label: '4+ cups' },
  ];
  // Sugary / fizzy drinks — single-select 4 buckets. Warm orange palette,
  // distinct from caffeine's amber.
  const SUGARY_DRINK_OPTIONS = [
    { value: 'none',  label: 'None' },
    { value: '1',     label: '1 drink' },
    { value: '2_3',   label: '2–3 drinks' },
    { value: '4plus', label: '4+ drinks' },
  ];
  // Medication & pain relief — single-select. Teal palette for a calm,
  // medical-adjacent feel without alarming colors. Strictly educational.
  const MEDICATION_OPTIONS = [
    { value: 'no',          label: 'No' },
    { value: 'prescribed',  label: 'Yes – prescribed medication' },
    { value: 'otc',         label: 'Yes – over-the-counter medication' },
    { value: 'pain_relief', label: 'Yes – pain relief medication' },
  ];
  // Nutrition habits — 3-level felt meal balance + 4-bucket optional
  // fruit & veg servings. Warm green palette (distinct from activity's
  // emerald and the basics-blue family).
  const MEAL_BALANCE_OPTIONS = [
    { value: 'very_balanced',     label: 'Very balanced' },
    { value: 'moderately',        label: 'Moderately balanced' },
    { value: 'needs_improvement', label: 'Needs improvement' },
  ];
  const FRUIT_VEG_OPTIONS = [
    { value: '0',     label: '0 servings' },
    { value: '1_2',   label: '1–2 servings' },
    { value: '3_5',   label: '3–5 servings' },
    { value: '5plus', label: '5+ servings' },
  ];
  // Stress check-in — 5-level felt scale + 6 optional coping chips.
  // Cool slate palette: calm, supportive, not alarming.
  const STRESS_LEVEL_OPTIONS = [
    { value: 'very_low',  label: 'Very low' },
    { value: 'mild',      label: 'Mild' },
    { value: 'moderate',  label: 'Moderate' },
    { value: 'high',      label: 'High' },
    { value: 'very_high', label: 'Very high' },
  ];
  const COPING_OPTIONS = [
    { value: 'deep_breathing', label: 'Deep breathing' },
    { value: 'rest',           label: 'Rest' },
    { value: 'exercise',       label: 'Exercise' },
    { value: 'talking',        label: 'Talking to someone' },
    { value: 'meditation',     label: 'Meditation' },
    { value: 'none',           label: 'None' },
  ];
  // General wellness symptoms — multi-select. "None" is a regular chip
  // we treat as exclusive on the client (picking "None" clears the
  // others; picking any other clears "None") to avoid the contradictory
  // "Headache + None" combination in tester data. Violet palette stays
  // calm; never alarming.
  const SYMPTOM_OPTIONS = [
    { value: 'headache',  label: 'Headache' },
    { value: 'fatigue',   label: 'Fatigue' },
    { value: 'dizziness', label: 'Dizziness' },
    { value: 'body_pain', label: 'Body pain' },
    { value: 'fever',     label: 'Fever' },
    { value: 'none',      label: 'None' },
  ];
  // Lifestyle habits — multi-select chips. Mirrors the SYMPTOM_OPTIONS
  // pattern: tapping `none` clears the others; tapping any item clears
  // `none`. Order chosen to put the most-frequently logged options first.
  const LIFESTYLE_OPTIONS = [
    { value: 'smoked_tobacco', labelKey: 'smoked_tobacco' },
    { value: 'vaping',         labelKey: 'vaping' },
    { value: 'alcohol',        labelKey: 'alcohol' },
    { value: 'fast_food',      labelKey: 'fast_food' },
    { value: 'energy_drinks',  labelKey: 'energy_drinks' },
    { value: 'none',           labelKey: 'none' },
  ];
  // Energy level — 4-level felt scale. Warm yellow palette.
  const ENERGY_LEVEL_OPTIONS = [
    { value: 'very_energetic',  label: 'Very energetic' },
    { value: 'good',            label: 'Good' },
    { value: 'low',             label: 'Low' },
    { value: 'extremely_tired', label: 'Extremely tired' },
  ];
  // Emotional wellness options — multi-select chips. Calming rose palette.
  // The "negative" set drives a more supportive nudge variant that gently
  // encourages reaching out for professional support if feelings persist.
  const EMOTION_OPTIONS = [
    { value: 'calm',        label: 'Calm',        tone: 'positive' },
    { value: 'happy',       label: 'Happy',       tone: 'positive' },
    { value: 'stressed',    label: 'Stressed',    tone: 'negative' },
    { value: 'anxious',     label: 'Anxious',     tone: 'negative' },
    { value: 'low',         label: 'Low',         tone: 'negative' },
    { value: 'overwhelmed', label: 'Overwhelmed', tone: 'negative' },
  ];

  // Keep the legacy yes/no `water` flag synced to the new 4-level
  // hydrationLevel so downstream AI insight + completion-task logic
  // (water === 'yes' | 'no') stays untouched. "Very/Moderately hydrated"
  // → yes, "Slightly/Very dehydrated" → no.
  useEffect(() => {
    if (!hydrationLevel) {
      setWater(null);
      return;
    }
    const hydrated = hydrationLevel === 'very_hydrated' || hydrationLevel === 'moderately';
    setWater(hydrated ? 'yes' : 'no');
  }, [hydrationLevel]);

  // Same pattern for sleep: keep the legacy yes/no `sleep` flag synced
  // from the new 4-level sleepQuality. Excellent/Good → yes, Fair/Poor → no.
  // Preserves the existing AI insight rules (e.g. sleep==='no' && stress==='high')
  // and the basics-section completion gating.
  useEffect(() => {
    if (!sleepQuality) {
      setSleep(null);
      return;
    }
    const restful = sleepQuality === 'excellent' || sleepQuality === 'good';
    setSleep(restful ? 'yes' : 'no');
  }, [sleepQuality]);

  // Keep the legacy 3-bucket `energy` flag synced from the new 4-level
  // energyLevel. very_energetic → 'high', good → 'okay', low /
  // extremely_tired → 'low'. Preserves the AI insight rules
  // (sleep === 'no' && energy === 'low') and mind-section completion
  // gating (!!(stress && mood && energy)).
  useEffect(() => {
    if (!energyLevel) {
      setEnergy(null);
      return;
    }
    if (energyLevel === 'very_energetic') {
      setEnergy('high');
    } else if (energyLevel === 'good') {
      setEnergy('okay');
    } else {
      setEnergy('low');
    }
  }, [energyLevel]);

  // Same pattern for food: keep the legacy yes/no `food` flag synced
  // from mealBalance. Very/Moderately balanced → 'yes', Needs improvement
  // → 'no'. Preserves AI insight rules and completion gating.
  useEffect(() => {
    if (!mealBalance) {
      setFood(null);
    setMealBalance(null);
    setFruitVegServings(null);
      return;
    }
    const balanced = mealBalance === 'very_balanced' || mealBalance === 'moderately';
    setFood(balanced ? 'yes' : 'no');
  }, [mealBalance]);

  // Keep the legacy 3-bucket `activity` flag synced from the new 4-level
  // activityLevel. Preserves AI insight rules that check `activity` and
  // the basics-section completion gating (sleep && water && food && activity).
  //   very_active / moderately → 'active'
  //   lightly                   → 'light'
  //   sedentary                 → 'not_active'
  useEffect(() => {
    if (!activityLevel) {
      setActivity(null);
      return;
    }
    if (activityLevel === 'very_active' || activityLevel === 'moderately') {
      setActivity('active');
    } else if (activityLevel === 'lightly') {
      setActivity('light');
    } else {
      setActivity('not_active');
    }
  }, [activityLevel]);

  /**
   * "Resume where you left off" — local-only, MVP-safe.
   *
   * We stash a tiny draft of the current form into localStorage on every
   * change. On mount we restore it ONLY if it's from today, so we never
   * surface stale answers from a previous day. There's no account
   * dependency, no backend write, no cross-device sync — strictly the
   * same browser session / device.
   *
   * Bundle/perf cost is effectively zero: one tiny JSON.stringify per
   * keystroke-equivalent input change, capped to the 12 small primitives
   * the form tracks.
   */
  const DRAFT_KEY = 'healthloop.checkinDraft.v1';
  const [draftRestored, setDraftRestored] = useState(false);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [insights, setInsights] = useState(null);
  // Live snapshot fetched after submit so the insight screen reflects the
  // user's freshly updated tracker (score, streak, completion %) and the
  // backend's behaviour-aware AI insight.
  const [snapshot, setSnapshot] = useState(null);
  const [aiInsight, setAiInsight] = useState(null);

  const toggleFeeling = (feeling) => {
    setFeelings(prev =>
      prev.includes(feeling)
        ? prev.filter(f => f !== feeling)
        : [...prev, feeling]
    );
  };

  const toggleExposure = (exposure) => {
    setExposures(prev =>
      prev.includes(exposure)
        ? prev.filter(e => e !== exposure)
        : [...prev, exposure]
    );
  };

  // ---- draft restore + autosave (local-only, today-only) ---------------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      const today = new Date().toISOString().split('T')[0];
      // Only restore drafts from today — yesterday's answers must not bleed
      // into today's check-in, and we never restore once the user has
      // already submitted (handleSubmit clears the draft below).
      if (draft?.date !== today) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      // Hydrate state. Each setter guards against undefined so a partial
      // older draft schema can't accidentally null-out current defaults.
      if (Array.isArray(draft.feelings)) setFeelings(draft.feelings);
      if (draft.sleep != null) setSleep(draft.sleep);
      if (draft.sleepQuality != null) setSleepQuality(draft.sleepQuality);
      if (draft.sleepDuration != null) setSleepDuration(draft.sleepDuration);
      if (draft.water != null) setWater(draft.water);
      if (draft.hydrationLevel != null) setHydrationLevel(draft.hydrationLevel);
      if (draft.waterIntake != null) setWaterIntake(draft.waterIntake);
      if (draft.food != null) setFood(draft.food);
      if (draft.mealBalance != null) setMealBalance(draft.mealBalance);
      if (draft.fruitVegServings != null) setFruitVegServings(draft.fruitVegServings);
      if (draft.activity != null) setActivity(draft.activity);
      if (draft.activityLevel != null) setActivityLevel(draft.activityLevel);
      if (Array.isArray(draft.activityTypes)) setActivityTypes(draft.activityTypes);
      if (draft.caffeineIntake != null) setCaffeineIntake(draft.caffeineIntake);
      if (draft.sugaryDrinks != null) setSugaryDrinks(draft.sugaryDrinks);
      if (draft.medicationToday != null) setMedicationToday(draft.medicationToday);
      if (typeof draft.medicationNote === 'string') setMedicationNote(draft.medicationNote);
      if (Array.isArray(draft.emotions)) setEmotions(draft.emotions);
      if (typeof draft.emotionsNote === 'string') setEmotionsNote(draft.emotionsNote);
      if (draft.stress != null) setStress(draft.stress);
      if (draft.stressLevel != null) setStressLevel(draft.stressLevel);
      if (Array.isArray(draft.copingActivities)) setCopingActivities(draft.copingActivities);
      if (Array.isArray(draft.lifestyleHabits)) setLifestyleHabits(draft.lifestyleHabits);
      if (draft.mood != null) setMood(draft.mood);
      if (draft.energy != null) setEnergy(draft.energy);
      if (draft.energyLevel != null) setEnergyLevel(draft.energyLevel);
      if (draft.routine != null) setRoutine(draft.routine);
      if (draft.environment != null) setEnvironment(draft.environment);
      if (draft.environmentAffected != null) setEnvironmentAffected(draft.environmentAffected);
      if (Array.isArray(draft.exposures)) setExposures(draft.exposures);
      // Only surface the "Picked up where you left off" hint if at least
      // one meaningful field was restored — silent restore otherwise.
      const hasContent =
        (Array.isArray(draft.feelings) && draft.feelings.length > 0) ||
        draft.sleep || draft.water || draft.food || draft.activity ||
        draft.stress || draft.mood || draft.energy ||
        draft.routine || draft.environment || draft.environmentAffected != null ||
        (Array.isArray(draft.exposures) && draft.exposures.length > 0);
      if (hasContent) setDraftRestored(true);
    } catch (_) {
      // Corrupt draft — drop it silently rather than surface an error.
      try { localStorage.removeItem(DRAFT_KEY); } catch (__) {}
    }
    // Intentional: run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Autosave a compact, today-stamped draft on every field change.
    // We deliberately skip persistence while the insight screen is showing
    // — at that point the form is "submitted" from the user's POV and the
    // submit handler has already cleared the draft.
    try {
      const today = new Date().toISOString().split('T')[0];
      const draft = {
        date: today,
        savedAt: new Date().toISOString(),
        feelings, sleep, sleepQuality, sleepDuration, water, hydrationLevel, waterIntake,
        food, activity, activityLevel, activityTypes,
        mealBalance, fruitVegServings,
        caffeineIntake, sugaryDrinks,
        medicationToday, medicationNote,
        emotions, emotionsNote,
        stress, stressLevel, copingActivities,
        symptoms,
        lifestyleHabits,
        mood, energy, energyLevel, routine,
        environment, environmentAffected, exposures,
      };
      // Don't even create the key until the user has interacted with at
      // least one field — keeps localStorage clean for users who just
      // open and close the page.
      const hasAnyContent =
        feelings.length > 0 || sleep || water || food || activity ||
        (Array.isArray(activityTypes) && activityTypes.length > 0) ||
        caffeineIntake || sugaryDrinks ||
        medicationToday || medicationNote ||
        (Array.isArray(emotions) && emotions.length > 0) || emotionsNote ||
        stress || mood || energy || routine ||
        environment || environmentAffected !== null || exposures.length > 0 ||
        (Array.isArray(lifestyleHabits) && lifestyleHabits.length > 0);
      if (hasAnyContent) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      }
    } catch (_) {}
  }, [feelings, sleep, sleepQuality, sleepDuration, water, hydrationLevel, waterIntake, food, activity, activityLevel, activityTypes, mealBalance, fruitVegServings, caffeineIntake, sugaryDrinks, medicationToday, medicationNote, emotions, emotionsNote, stress, stressLevel, copingActivities, symptoms, lifestyleHabits, mood, energy, energyLevel, routine, environment, environmentAffected, exposures]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
  };

  const handleStartFresh = () => {
    setFeelings([]);
    setSleep(null);
    setWater(null);
    setHydrationLevel(null);
    setWaterIntake(null);
    setFood(null);
    setMealBalance(null);
    setFruitVegServings(null);
    setActivity(null);
    setActivityLevel(null);
    setActivityTypes([]);
    setCaffeineIntake(null);
    setSugaryDrinks(null);
    setEmotions([]);
    setEmotionsNote('');
    setStress(null);
    setStressLevel(null);
    setCopingActivities([]);
    setSymptoms([]);
    setMood(null);
    setEnergy(null);
    setEnergyLevel(null);
    setRoutine(null);
    setEnvironment(null);
    setEnvironmentAffected(null);
    setExposures([]);
    setDraftRestored(false);
    clearDraft();
  };

  const generateInsights = () => {
    // Insight generation logic based on inputs
    let opening = "Based on your check-in today...";
    let patterns = [];
    let actions = [];
    let seekHelp = [];

    // Analyze sleep + stress + energy
    if (sleep === 'no' && stress === 'high') {
      patterns.push("Poor sleep and high stress may be affecting your energy levels");
      actions.push("Try to get 7-8 hours of sleep tonight");
      actions.push("Practice a 5-minute breathing exercise");
    } else if (sleep === 'no' && energy === 'low') {
      patterns.push("Lack of rest is likely impacting your energy");
      actions.push("Take a short rest or nap if possible");
      actions.push("Go to bed 30 minutes earlier tonight");
    }

    // Analyze activity + routine
    if (activity === 'not_active' && routine === 'no') {
      patterns.push("Low activity and a disrupted routine may be contributing to how you feel");
      actions.push("Try light movement or a short walk");
      actions.push("Stick to your regular bedtime tonight");
    }

    // Analyze environment + feelings
    if (environmentAffected === 'yes' && (exposures.includes('heat') || exposures.includes('cold') || exposures.includes('dust_smoke'))) {
      patterns.push("Your environment and exposure factors may be adding to your discomfort");
      actions.push("If possible, stay in a clean and well-ventilated space");
      actions.push("Stay hydrated, especially in challenging conditions");
    }

    // Analyze water + energy
    if (water === 'no' && energy === 'low') {
      patterns.push("Low hydration may be affecting your energy levels");
      actions.push("Drink 2 glasses of water right now");
      actions.push("Keep water nearby throughout the day");
    }

    // Analyze stress + environment
    if (stress === 'high' && (exposures.includes('noise') || exposures.includes('crowded'))) {
      patterns.push("Noisy or crowded surroundings combined with stress may be overwhelming you");
      actions.push("Take short breaks from noisy or crowded spaces");
      actions.push("Find a quiet moment to reset");
    }

    // Analyze mood + food
    if (mood === 'low' && food === 'no') {
      patterns.push("Skipping meals may be affecting your mood and energy");
      actions.push("Have a balanced meal or healthy snack");
      actions.push("Don't skip meals, even when busy");
    }

    // Default insights if no specific patterns detected
    if (patterns.length === 0) {
      if (feelings.includes('okay')) {
        patterns.push("You're doing well today! Keep up with your healthy habits");
        actions.push("Stay consistent with sleep, water, and activity");
        actions.push("Take time for activities you enjoy");
      } else {
        patterns.push("Your body and mind are working hard today");
        actions.push("Listen to what your body needs");
        actions.push("Don't push yourself too hard");
      }
    }

    // When to seek help (always include)
    seekHelp = [
      "If symptoms persist for more than 2-3 days",
      "If symptoms worsen",
      "If new symptoms appear"
    ];

    // Limit actions to max 4
    if (actions.length > 4) {
      actions = actions.slice(0, 4);
    }

    return {
      opening,
      patterns: patterns.slice(0, 2), // Max 2 patterns
      actions,
      seekHelp
    };
  };

  /**
   * Persist the check-in via the local-first queue so the submit never
   * fails on a flaky connection. The queue ships {taskIds, smart} as a
   * single logical unit (atomic from the user's POV — either both writes
   * land or neither does on this round; the queue retries on the next
   * online event or manual refresh).
   *
   * Dedupe key in the queue (`date`) ensures a same-day re-submit only
   * ever has one entry parked locally — the latest values win.
   */
  const persistCheckIn = async () => {
    const taskIds = [];
    if (water === 'yes') taskIds.push('water');
    if (food === 'yes') taskIds.push('meals');
    if (sleep === 'yes') taskIds.push('sleep');
    if (activity === 'active' || activity === 'light') taskIds.push('steps');

    const smart = {
      feelings,
      sleep,
      sleep_quality: sleepQuality,
      sleep_duration: sleepDuration,
      water,
      hydration_level: hydrationLevel,
      water_intake: waterIntake,
      food,
      activity,
      activity_level: activityLevel,
      activity_types: activityTypes,
      caffeine_intake: caffeineIntake,
      sugary_drinks: sugaryDrinks,
      medication_today: medicationToday,
      medication_note: medicationNote || null,
      emotions,
      emotions_note: emotionsNote || null,
      stress,
      stress_level: stressLevel,
      coping_activities: copingActivities,
      symptoms,
      lifestyle_habits: lifestyleHabits,
      mood,
      energy,
      energy_level: energyLevel,
      routine,
      environment,
      environment_affected: environmentAffected,
      exposures,
    };

    const today = new Date().toISOString().split('T')[0];
    const result = await checkinQueue.enqueueAndSync({ date: today, taskIds, smart });
    return result;
  };

  const fetchPostSubmitSnapshot = async () => {
    // After persisting, fetch the freshly updated tracker AND the AI insight
    // so the insight screen reflects the new score/streak/completion %.
    try {
      const [trackerRes, aiRes] = await Promise.all([
        healthApi.getDailyTracker(),
        healthApi.getAIInsights().catch(() => ({ data: { insights: [] } })),
      ]);
      setSnapshot(trackerRes?.data?.tracker || null);
      const top = aiRes?.data?.insights?.[0] || null;
      setAiInsight(top);
    } catch (_) {
      // Snapshot is optional UI sugar; never block the insight screen on it.
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    // Cache today's submitted state locally so a hard reload after submit
    // doesn't lose the user's effort — the insights screen can rehydrate
    // from this snapshot if the user refreshes before backend sync.
    try {
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('healthloop.lastCheckIn', JSON.stringify({
        date: today,
        savedAt: new Date().toISOString(),
        answers: { feelings, sleep, sleepQuality, sleepDuration, water, hydrationLevel, waterIntake, food, mealBalance, fruitVegServings, activity, activityLevel, activityTypes, caffeineIntake, sugaryDrinks, medicationToday, medicationNote, emotions, emotionsNote, stress, stressLevel, copingActivities, symptoms, lifestyleHabits, mood, energy, energyLevel, routine, environment, environmentAffected, exposures },
      }));
    } catch (_) {}

    // Run insight generation in parallel with persistence — the user never
    // waits longer than necessary for the warm screen to appear.
    const [generatedInsights, persistResult] = await Promise.all([
      (async () => {
        await new Promise((resolve) => setTimeout(resolve, 600));
        return generateInsights();
      })(),
      persistCheckIn(),
    ]);

    // Snapshot pull is online-only — when offline we keep the optimistic
    // insight view instead of bouncing the user with a network error.
    if (isOnline() && persistResult?.ok) {
      await fetchPostSubmitSnapshot();
    }

    setInsights(generatedInsights);
    setLoading(false);
    setShowInsights(true);

    // Honest, state-specific feedback — no fake "sync in progress".
    if (persistResult?.ok) {
      toast.success(t('checkin.toast.savedFull'));
    } else if (persistResult?.queued) {
      toast.message(t('checkin.toast.queued', "Saved locally. We'll sync when you're back online."));
    } else {
      toast.success(t('checkin.toast.savedShort'));
    }
    // Submission successful from the user's POV — clear the draft so the
    // "Picked up where you left off" hint doesn't reappear on a future visit
    // until a new in-progress check-in is started.
    clearDraft();
    setDraftRestored(false);
  };

  const handleNewCheckIn = () => {
    // Reset all states
    setFeelings([]);
    setSleep(null);
    setSleepQuality(null);
    setSleepDuration(null);
    setWater(null);
    setHydrationLevel(null);
    setWaterIntake(null);
    setFood(null);
    setMealBalance(null);
    setFruitVegServings(null);
    setActivity(null);
    setActivityLevel(null);
    setActivityTypes([]);
    setCaffeineIntake(null);
    setSugaryDrinks(null);
    setEmotions([]);
    setEmotionsNote('');
    setStress(null);
    setStressLevel(null);
    setCopingActivities([]);
    setSymptoms([]);
    setMood(null);
    setEnergy(null);
    setEnergyLevel(null);
    setRoutine(null);
    setEnvironment(null);
    setEnvironmentAffected(null);
    setExposures([]);
    setShowInsights(false);
    setInsights(null);
    setSnapshot(null);
    setAiInsight(null);
  };

  // ----- "I'm not feeling it today" rest-day flow -----
  const [restDay, setRestDay] = useState(null); // null | "submitting" | "done"
  const handleRestDay = async () => {
    if (restDay === "submitting" || restDay === "done") return;
    setRestDay("submitting");
    try {
      await healthApi.recordRestDay();
      setRestDay("done");
    } catch (_) {
      // Even if the network failed, honour the user's choice — don't bounce
      // them back into the form. Show the calm screen anyway.
      setRestDay("done");
    }
    // The user has explicitly chosen to skip today — drop any in-progress
    // draft so we don't prompt them to resume tomorrow.
    clearDraft();
    setDraftRestored(false);
  };

  if (restDay === "done") {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 flex items-center px-6"
        data-testid="checkin-restday-screen"
      >
        <div className="max-w-md mx-auto text-center space-y-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <Moon className="h-9 w-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {t('checkin.restDay.title')}
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            {t('checkin.restDay.body')}
          </p>
          <p className="text-sm text-slate-500 italic">
            {t('checkin.restDay.streakNote')}
          </p>
          <div className="pt-4 flex flex-col gap-2">
            <Button
              onClick={() => navigate('/dashboard')}
              className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
              data-testid="checkin-restday-back-btn"
            >
              {t('checkin.restDay.backToDashboard')}
            </Button>
            <button
              onClick={() => setRestDay(null)}
              className="text-sm text-slate-500 hover:text-slate-800 mt-2"
              data-testid="checkin-restday-changed-mind-btn"
            >
              {t('checkin.restDay.changedMind')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showInsights && insights) {
    return <InsightsScreen
      insights={insights}
      snapshot={snapshot}
      aiInsight={aiInsight}
      onBack={() => navigate('/dashboard')}
      onNew={handleNewCheckIn}
    />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 overflow-y-auto pb-32">
      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {t('checkin.header.title')}
          </h1>
          <p className="text-sm text-slate-600">
            {t('checkin.header.subtitle')}
          </p>
          {/* Sync-status pill — only renders when there's something honest
              to say (offline, queued updates, paused). Stays invisible in
              the happy path so it never feels like noise. */}
          <div className="mt-3 flex justify-center">
            <SyncStatusPill queue={checkinQueue} data-testid="checkin-sync-status" />
          </div>
          <button
            onClick={handleRestDay}
            disabled={restDay === "submitting"}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 underline-offset-4 hover:underline transition-colors disabled:opacity-60"
            data-testid="checkin-restday-toggle"
          >
            <Heart className="h-3.5 w-3.5" />
            {t('checkin.header.restDayLink')}
          </button>
        </div>

        {/* "Picked up where you left off" — subtle, dismissible, only renders
            when an in-progress draft from today was actually restored on
            mount. Tapping "Start fresh" wipes the draft + form state. */}
        {draftRestored && (
          <div
            className="mx-auto max-w-md flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800"
            data-testid="checkin-resume-banner"
            role="status"
          >
            <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="flex-1 min-w-0">{t('checkin.resume.banner')}</span>
            <button
              type="button"
              onClick={handleStartFresh}
              className="underline underline-offset-2 hover:text-emerald-900 whitespace-nowrap"
              data-testid="checkin-resume-start-fresh"
            >
              {t('checkin.resume.startFresh')}
            </button>
            <button
              type="button"
              onClick={() => setDraftRestored(false)}
              className="rounded-full p-0.5 hover:bg-white/60 transition-colors"
              aria-label={t('checkin.resume.dismiss')}
              data-testid="checkin-resume-dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Progress indicator — calm, sticky-friendly "Step X of 5" with a
            thin bar. Computed from form state, so it reflects what the user
            has already answered without any extra taps or backend hits.
            Sections are considered "complete" when their core questions
            are answered (exposures stays optional — it's a multi-select). */}
        {(() => {
          const sectionsCompleted = [
            feelings.length > 0,                                    // 1) feelings
            !!(sleep && water && food && activity),                 // 2) basics
            !!(stress && mood && energy),                           // 3) mind & energy
            !!routine,                                              // 4) your day
            !!(environment && environmentAffected !== null),        // 5) environment
          ].filter(Boolean).length;
          const totalSections = 5;
          const currentStep = Math.min(sectionsCompleted + 1, totalSections);
          const allDone = sectionsCompleted === totalSections;
          const pct = (sectionsCompleted / totalSections) * 100;
          return (
            <div
              className="sticky top-2 z-10 mx-auto rounded-full bg-white/90 backdrop-blur border border-emerald-100 shadow-sm px-3 py-1.5 flex items-center gap-3 max-w-xs"
              data-testid="checkin-progress-indicator"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={totalSections}
              aria-valuenow={sectionsCompleted}
              aria-label={t('checkin.progress.ariaLabel', { done: sectionsCompleted, total: totalSections })}
            >
              <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap" data-testid="checkin-progress-label">
                {allDone ? t('checkin.progress.allSet') : t('checkin.progress.step', { current: currentStep, total: totalSections })}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-emerald-50 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-300 ease-out"
                  style={{ width: `${pct}%` }}
                  data-testid="checkin-progress-fill"
                />
              </div>
            </div>
          );
        })()}

        {/* SECTION 1: How are you feeling */}
        <Card className="border-2 border-purple-200 shadow-lg rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.feelings.title')}</CardTitle>
            <p className="text-sm text-gray-600">{t('checkin.feelings.hint')}</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { id: 'tired',    labelKey: 'checkin.feelings.tired' },
              { id: 'headache', labelKey: 'checkin.feelings.headache' },
              { id: 'stressed', labelKey: 'checkin.feelings.stressed' },
              { id: 'pain',     labelKey: 'checkin.feelings.pain' },
              { id: 'okay',     labelKey: 'checkin.feelings.okay' },
            ].map(feeling => (
              <button
                key={feeling.id}
                onClick={() => toggleFeeling(feeling.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  feelings.includes(feeling.id)
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  {feelings.includes(feeling.id) ? (
                    <CheckCircle2 className="h-5 w-5 text-purple-600" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="font-medium">{t(feeling.labelKey)}</span>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* SECTION 2: Your basics */}
        <Card className="border-2 border-blue-200 shadow-lg rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.basics.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            
            {/* Sleep — felt quality (4 options) + optional duration.
                Mirrors the Hydration UX: 4-option grid + optional 4-bucket
                duration that only appears once quality is chosen, plus a
                calm, supportive wellness nudge. Updates the legacy `sleep`
                yes/no via the sync effect at the top of the component, so
                AI insights and task completion continue to work unchanged. */}
            <div data-testid="sleep-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.sleepQualityQ')}
              </p>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Sleep quality">
                {SLEEP_QUALITY_OPTIONS.map((opt) => {
                  const active = sleepQuality === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setSleepQuality(opt.value)}
                      data-testid={`sleep-quality-${opt.value}`}
                      className={`p-3 rounded-xl border-2 text-sm font-medium text-center transition-all active:scale-[0.98] ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                          : 'border-gray-200 hover:border-indigo-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.sleepQuality_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Optional duration — appears once quality is chosen. Taps
                  on the same option deselect it (truly optional). */}
              {sleepQuality && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">{t('checkin.basics.sleepDurationQ')}</span>
                    <span className="text-gray-500"> · {t('checkin.basics.optional')}</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Sleep duration">
                    {SLEEP_DURATION_OPTIONS.map((opt) => {
                      const active = sleepDuration === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setSleepDuration(active ? null : opt.value)}
                          data-testid={`sleep-duration-${opt.value}`}
                          className={`py-2 px-2 rounded-lg border text-xs sm:text-sm font-medium transition-all active:scale-[0.98] ${
                            active
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                              : 'border-gray-200 hover:border-indigo-300 text-gray-700 bg-white'
                          }`}
                        >
                          {t(`checkin.basics.sleepDuration_${opt.value}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Calm, supportive rest-and-recovery nudge. Tone is gently
                  reassuring and shifts subtly when the user reports poor
                  sleep — still non-diagnostic, no clinical claims. */}
              <div
                className="mt-3 rounded-lg bg-indigo-50/60 border border-indigo-100 px-3 py-2 text-xs text-indigo-900 leading-relaxed"
                data-testid="sleep-nudge"
              >
                {sleepQuality === 'poor' || sleepQuality === 'fair'
                  ? t('checkin.basics.sleepNudgeRest')
                  : t('checkin.basics.sleepNudge')}
              </div>
            </div>

            {/* Hydration — felt level (4 options) + optional water intake.
                Educational, non-diagnostic copy. Mobile-first 2x2 grid.
                Updates the legacy `water` yes/no via the sync effect at
                the top of the component, so AI insights and task completion
                continue to work unchanged. */}
            <div data-testid="hydration-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.hydrationQ')}
              </p>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Hydration level">
                {HYDRATION_OPTIONS.map((opt) => {
                  const active = hydrationLevel === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setHydrationLevel(opt.value)}
                      data-testid={`hydration-${opt.value}`}
                      className={`p-3 rounded-xl border-2 text-sm font-medium text-center transition-all active:scale-[0.98] ${
                        active
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-200 hover:border-blue-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.hydration_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Optional water intake — appears once the user has answered
                  the hydration question, to avoid front-loading the form.
                  "Skip" stays implicit since the field is optional. */}
              {hydrationLevel && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">{t('checkin.basics.waterIntakeQ')}</span>
                    <span className="text-gray-500"> · {t('checkin.basics.optional')}</span>
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Water intake">
                    {WATER_INTAKE_OPTIONS.map((opt) => {
                      const active = waterIntake === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setWaterIntake(active ? null : opt.value)}
                          data-testid={`water-intake-${opt.value}`}
                          className={`py-2 px-2 rounded-lg border text-xs sm:text-sm font-medium transition-all active:scale-[0.98] ${
                            active
                              ? 'border-blue-500 bg-blue-50 text-blue-900'
                              : 'border-gray-200 hover:border-blue-300 text-gray-700 bg-white'
                          }`}
                        >
                          {t(`checkin.basics.waterIntake_${opt.value}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Gentle, educational wellness nudge — non-diagnostic. */}
              <div
                className="mt-3 rounded-lg bg-blue-50/60 border border-blue-100 px-3 py-2 text-xs text-blue-900 leading-relaxed"
                data-testid="hydration-nudge"
              >
                {t('checkin.basics.hydrationNudge')}
              </div>
            </div>

            {/* Nutrition habits — felt meal balance (3 options) + optional
                fruit & veg servings tracker. Replaces the legacy yes/no
                food question. Encouraging, non-diagnostic; the nudge
                shifts to a gentle "small step" reminder when the user
                reports their meals needed improvement. The legacy `food`
                yes/no flag is kept in sync via the effect at the top. */}
            <div data-testid="nutrition-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.mealBalanceQ')}
              </p>
              <div className="space-y-2" role="radiogroup" aria-label="Meal balance">
                {MEAL_BALANCE_OPTIONS.map((opt) => {
                  const active = mealBalance === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setMealBalance(opt.value)}
                      data-testid={`meal-balance-${opt.value}`}
                      className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all active:scale-[0.99] ${
                        active
                          ? 'border-green-500 bg-green-50 text-green-900'
                          : 'border-gray-200 hover:border-green-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.mealBalance_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Optional fruit & veg servings — appears once meal balance
                  is chosen. 2x2 grid; re-tap deselects. */}
              {mealBalance && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">{t('checkin.basics.fruitVegQ')}</span>
                    <span className="text-gray-500"> · {t('checkin.basics.optional')}</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Fruit and vegetable servings">
                    {FRUIT_VEG_OPTIONS.map((opt) => {
                      const active = fruitVegServings === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setFruitVegServings(active ? null : opt.value)}
                          data-testid={`fruit-veg-${opt.value}`}
                          className={`py-2 px-2 rounded-lg border text-xs sm:text-sm font-medium transition-all active:scale-[0.98] ${
                            active
                              ? 'border-green-500 bg-green-50 text-green-900'
                              : 'border-gray-200 hover:border-green-300 text-gray-700 bg-white'
                          }`}
                        >
                          {t(`checkin.basics.fruitVeg_${opt.value}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Encouraging healthy-eating nudge. Two variants — a
                  positive baseline reinforcing balanced choices, and a
                  gentler "small step" message when meals needed
                  improvement. Both stay educational, never restrictive. */}
              <div
                className="mt-3 rounded-lg bg-green-50/60 border border-green-100 px-3 py-2 text-xs text-green-900 leading-relaxed"
                data-testid="nutrition-nudge"
              >
                {mealBalance === 'needs_improvement'
                  ? t('checkin.basics.nutritionNudgeGentle')
                  : t('checkin.basics.nutritionNudge')}
              </div>
            </div>

            {/* Activity — felt level (4 options) + optional multi-select
                activity types. Mirrors the Hydration/Sleep UX with an emerald
                palette to keep the basics section visually progressive. The
                legacy `activity` 3-bucket value is kept in sync via the effect
                at the top, so AI insights and basics completion gating still
                work. Encouraging, preventive, non-diagnostic copy. */}
            <div data-testid="activity-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.activityLevelQ')}
              </p>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Activity level">
                {ACTIVITY_LEVEL_OPTIONS.map((opt) => {
                  const active = activityLevel === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setActivityLevel(opt.value)}
                      data-testid={`activity-level-${opt.value}`}
                      className={`p-3 rounded-xl border-2 text-sm font-medium text-center transition-all active:scale-[0.98] ${
                        active
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                          : 'border-gray-200 hover:border-emerald-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.activityLevel_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Optional activity types — multi-select chips. Appears once
                  a level has been chosen so we don't front-load the form. */}
              {activityLevel && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">{t('checkin.basics.activityTypesQ')}</span>
                    <span className="text-gray-500"> · {t('checkin.basics.optional')}</span>
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Activity types">
                    {ACTIVITY_TYPE_OPTIONS.map((opt) => {
                      const active = activityTypes.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          aria-pressed={active}
                          onClick={() => {
                            setActivityTypes((prev) =>
                              prev.includes(opt.value)
                                ? prev.filter((v) => v !== opt.value)
                                : [...prev, opt.value]
                            );
                          }}
                          data-testid={`activity-type-${opt.value}`}
                          className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-all active:scale-[0.97] ${
                            active
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                              : 'border-gray-200 hover:border-emerald-300 text-gray-700 bg-white'
                          }`}
                        >
                          {t(`checkin.basics.activityType_${opt.value}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Encouraging preventive-wellness nudge. Tone shifts gently
                  when the user reports a sedentary day — still warm and
                  non-prescriptive. */}
              <div
                className="mt-3 rounded-lg bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-xs text-emerald-900 leading-relaxed"
                data-testid="activity-nudge"
              >
                {activityLevel === 'sedentary'
                  ? t('checkin.basics.activityNudgeGentle')
                  : t('checkin.basics.activityNudge')}
              </div>
            </div>

            {/* Caffeine intake — lightweight 4-bucket tracker with an
                educational nudge that touches on sleep, hydration, and
                energy balance. Single-select 2x2 grid for fast daily taps.
                Tone is informational, non-diagnostic; the higher-intake
                variant adds a gentle reminder about pairing with water
                and earlier wind-down. */}
            <div data-testid="caffeine-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.caffeineQ')}
              </p>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Caffeine intake">
                {CAFFEINE_OPTIONS.map((opt) => {
                  const active = caffeineIntake === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setCaffeineIntake(opt.value)}
                      data-testid={`caffeine-${opt.value}`}
                      className={`p-3 rounded-xl border-2 text-sm font-medium text-center transition-all active:scale-[0.98] ${
                        active
                          ? 'border-amber-500 bg-amber-50 text-amber-900'
                          : 'border-gray-200 hover:border-amber-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.caffeine_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Two nudge variants: a gentle "balance" reminder for
                  higher intakes (2-3 / 4+), and a positive-baseline
                  educational message otherwise. Both stay educational. */}
              <div
                className="mt-3 rounded-lg bg-amber-50/60 border border-amber-100 px-3 py-2 text-xs text-amber-900 leading-relaxed"
                data-testid="caffeine-nudge"
              >
                {caffeineIntake === '2_3' || caffeineIntake === '4plus'
                  ? t('checkin.basics.caffeineNudgeBalance')
                  : t('checkin.basics.caffeineNudge')}
              </div>
            </div>

            {/* Sugary / fizzy drinks — lightweight 4-bucket tracker with
                a preventive-wellness education nudge. Tone shifts to a
                gentler reminder about hydration, energy swings, and
                pacing when intake is higher. Non-diagnostic, no scare
                language — orange palette keeps it visually distinct from
                caffeine's amber. */}
            <div data-testid="sugary-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.sugaryQ')}
              </p>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Sugary drinks">
                {SUGARY_DRINK_OPTIONS.map((opt) => {
                  const active = sugaryDrinks === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setSugaryDrinks(opt.value)}
                      data-testid={`sugary-${opt.value}`}
                      className={`p-3 rounded-xl border-2 text-sm font-medium text-center transition-all active:scale-[0.98] ${
                        active
                          ? 'border-orange-500 bg-orange-50 text-orange-900'
                          : 'border-gray-200 hover:border-orange-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.sugary_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Two nudge variants: a gentle "balance" reminder for
                  higher intakes (2-3 / 4+), and a positive baseline
                  educational message otherwise. Both stay educational. */}
              <div
                className="mt-3 rounded-lg bg-orange-50/60 border border-orange-100 px-3 py-2 text-xs text-orange-900 leading-relaxed"
                data-testid="sugary-nudge"
              >
                {sugaryDrinks === '2_3' || sugaryDrinks === '4plus'
                  ? t('checkin.basics.sugaryNudgeBalance')
                  : t('checkin.basics.sugaryNudge')}
              </div>
            </div>

            {/* Medication & pain relief — strictly educational tracker.
                NEVER directive. The list of 4 options is single-select.
                An optional note textarea captures context without
                prescribing language. The supportive nudge becomes more
                explicit about consulting a healthcare professional when
                any "Yes" is selected. We deliberately avoid medication
                names, dosages, brand names, and side-effect language. */}
            <div data-testid="medication-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.basics.medicationQ')}
              </p>
              <div className="space-y-2" role="radiogroup" aria-label="Medication today">
                {MEDICATION_OPTIONS.map((opt) => {
                  const active = medicationToday === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setMedicationToday(opt.value)}
                      data-testid={`medication-${opt.value}`}
                      className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all active:scale-[0.99] ${
                        active
                          ? 'border-teal-500 bg-teal-50 text-teal-900'
                          : 'border-gray-200 hover:border-teal-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.basics.medication_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Optional follow-up note — only appears once the user has
                  answered, to avoid front-loading. maxLength caps payload
                  size. Placeholder is intentionally non-clinical. */}
              {medicationToday && medicationToday !== 'no' && (
                <div className="mt-3">
                  <label
                    htmlFor="medication-note"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('checkin.basics.medicationNoteLabel')}
                    <span className="text-gray-500 font-normal"> · {t('checkin.basics.optional')}</span>
                  </label>
                  <textarea
                    id="medication-note"
                    rows={3}
                    value={medicationNote}
                    onChange={(e) => setMedicationNote(e.target.value)}
                    maxLength={500}
                    placeholder={t('checkin.basics.medicationNotePlaceholder')}
                    data-testid="medication-note"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 placeholder-gray-400 resize-none"
                  />
                </div>
              )}

              {/* Supportive reminder — two variants. The "yes" variant
                  more explicitly encourages partnering with a healthcare
                  professional. Both are strictly educational with no
                  diagnosis, dosage, or treatment advice. */}
              <div
                className="mt-3 rounded-lg bg-teal-50/60 border border-teal-100 px-3 py-2.5 text-xs text-teal-900 leading-relaxed"
                data-testid="medication-nudge"
              >
                {medicationToday && medicationToday !== 'no'
                  ? t('checkin.basics.medicationNudgeYes')
                  : t('checkin.basics.medicationNudge')}
              </div>
            </div>

          </CardContent>
        </Card>

        {/* SECTION 2b: Emotional wellness — additive, non-blocking.
            Multi-select emotion chips + optional notes. Supportive,
            non-diagnostic tone; the nudge shifts to a gentler, more
            supportive variant (mentioning trusted people / professional
            support) when the user selects negative emotions. */}
        <Card className="border-2 border-rose-200 shadow-lg rounded-2xl" data-testid="emotional-section">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.emotional.title')}</CardTitle>
            <p className="text-sm text-gray-600">{t('checkin.emotional.q')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 6-emotion chip grid — 2 cols on mobile, 3 on sm+. Multi-select. */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" role="group" aria-label="Emotional wellness">
              {EMOTION_OPTIONS.map((opt) => {
                const active = emotions.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      setEmotions((prev) =>
                        prev.includes(opt.value)
                          ? prev.filter((v) => v !== opt.value)
                          : [...prev, opt.value]
                      );
                    }}
                    data-testid={`emotion-${opt.value}`}
                    className={`py-2.5 px-2 rounded-xl border-2 text-sm font-medium text-center transition-all active:scale-[0.98] ${
                      active
                        ? 'border-rose-500 bg-rose-50 text-rose-900'
                        : 'border-gray-200 hover:border-rose-300 text-gray-800 bg-white'
                    }`}
                  >
                    {t(`checkin.emotional.emotion_${opt.value}`)}
                  </button>
                );
              })}
            </div>

            {/* Optional free-text notes. We use rows={3} to set a calm
                visual baseline without forcing the keyboard to dominate
                the mobile viewport. maxLength=500 prevents accidental
                novel-length payloads. */}
            <div>
              <label
                htmlFor="emotions-note"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {t('checkin.emotional.noteLabel')}
                <span className="text-gray-500 font-normal"> · {t('checkin.basics.optional')}</span>
              </label>
              <textarea
                id="emotions-note"
                rows={3}
                value={emotionsNote}
                onChange={(e) => setEmotionsNote(e.target.value)}
                maxLength={500}
                placeholder={t('checkin.emotional.notePlaceholder')}
                data-testid="emotions-note"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400 placeholder-gray-400 resize-none"
              />
            </div>

            {/* Supportive wellness reminder. Two variants: a gentler
                self-care nudge by default; a more explicitly supportive
                message that points toward trusted people / a professional
                if any difficult emotions are selected. Strictly non-
                diagnostic — no clinical labels, no risk-stratification. */}
            <div
              className="rounded-lg bg-rose-50/60 border border-rose-100 px-3 py-2.5 text-xs text-rose-900 leading-relaxed"
              data-testid="emotional-nudge"
            >
              {emotions.some((e) => ['stressed', 'anxious', 'low', 'overwhelmed'].includes(e))
                ? t('checkin.emotional.nudgeSupportive')
                : t('checkin.emotional.nudgeSelfCare')}
            </div>
          </CardContent>
        </Card>

        {/* SECTION 3: Mind & energy */}
        <Card className="border-2 border-green-200 shadow-lg rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.mind.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            
            {/* Stress check-in — felt 5-level scale + optional 6-chip
                multi-select coping activities. Replaces the legacy
                low/moderate/high. Cool slate palette: calm, supportive,
                non-clinical. Legacy `stress` value is kept in sync via
                the effect at the top, so AI insight rules and mind-
                section completion gating still work. */}
            <div data-testid="stress-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.mind.stressLevelQ')}
              </p>
              <div className="space-y-2" role="radiogroup" aria-label="Stress level">
                {STRESS_LEVEL_OPTIONS.map((opt) => {
                  const active = stressLevel === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setStressLevel(opt.value)}
                      data-testid={`stress-level-${opt.value}`}
                      className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all active:scale-[0.99] ${
                        active
                          ? 'border-slate-500 bg-slate-50 text-slate-900'
                          : 'border-gray-200 hover:border-slate-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.mind.stressLevel_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Optional coping activities — multi-select chips. Appears
                  once stress level is chosen. "None" is a regular chip
                  the user can pick alongside or instead of others; we
                  don't make it exclusive because plenty of days are
                  simply "nothing in particular helped". */}
              {stressLevel && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    <span className="font-medium">{t('checkin.mind.copingQ')}</span>
                    <span className="text-gray-500"> · {t('checkin.basics.optional')}</span>
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Coping activities">
                    {COPING_OPTIONS.map((opt) => {
                      const active = copingActivities.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          aria-pressed={active}
                          onClick={() => {
                            setCopingActivities((prev) =>
                              prev.includes(opt.value)
                                ? prev.filter((v) => v !== opt.value)
                                : [...prev, opt.value]
                            );
                          }}
                          data-testid={`coping-${opt.value}`}
                          className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-all active:scale-[0.97] ${
                            active
                              ? 'border-slate-500 bg-slate-100 text-slate-900'
                              : 'border-gray-200 hover:border-slate-300 text-gray-700 bg-white'
                          }`}
                        >
                          {t(`checkin.mind.coping_${opt.value}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Supportive, non-clinical nudge. Two variants — a calm
                  baseline reminder about pausing/breathing/reaching out,
                  and a more explicitly supportive message when stress is
                  high or very high (gently suggests talking to someone
                  the user trusts; no clinical labels). */}
              <div
                className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-800 leading-relaxed"
                data-testid="stress-nudge"
              >
                {stressLevel === 'high' || stressLevel === 'very_high'
                  ? t('checkin.mind.stressNudgeSupportive')
                  : t('checkin.mind.stressNudge')}
              </div>
            </div>

            {/* Mood */}
            <div>
              <p className="font-medium text-gray-900 mb-3">{t('checkin.mind.moodQ')}</p>
              <div className="space-y-2">
                <button
                  onClick={() => setMood('low')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    mood === 'low' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.mind.low')}</span>
                </button>
                <button
                  onClick={() => setMood('neutral')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    mood === 'neutral' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.mind.neutral')}</span>
                </button>
                <button
                  onClick={() => setMood('good')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    mood === 'good' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.mind.good')}</span>
                </button>
              </div>
            </div>

            {/* Energy level — felt 4-level scale, replacing the legacy
                low/okay/high. Warm yellow palette. Legacy `energy` flag
                is kept in sync via the effect at the top so AI insight
                rules and mind-section completion gating still work. The
                nudge is two-variant: a positive baseline for good energy
                days, and a supportive "rest/refuel" reminder when the
                user reports low or extremely tired. */}
            <div data-testid="energy-section">
              <p className="font-medium text-gray-900 mb-3">
                {t('checkin.mind.energyLevelQ')}
              </p>
              <div className="space-y-2" role="radiogroup" aria-label="Energy level">
                {ENERGY_LEVEL_OPTIONS.map((opt) => {
                  const active = energyLevel === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setEnergyLevel(opt.value)}
                      data-testid={`energy-level-${opt.value}`}
                      className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all active:scale-[0.99] ${
                        active
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-900'
                          : 'border-gray-200 hover:border-yellow-300 text-gray-800'
                      }`}
                    >
                      {t(`checkin.mind.energyLevel_${opt.value}`)}
                    </button>
                  );
                })}
              </div>

              {/* Supportive guidance — touches on rest, hydration,
                  nutrition, and balance. Two variants. Non-diagnostic. */}
              <div
                className="mt-3 rounded-lg bg-yellow-50/60 border border-yellow-100 px-3 py-2.5 text-xs text-yellow-900 leading-relaxed"
                data-testid="energy-nudge"
              >
                {energyLevel === 'low' || energyLevel === 'extremely_tired'
                  ? t('checkin.mind.energyNudgeRest')
                  : t('checkin.mind.energyNudge')}
              </div>
            </div>

          </CardContent>
        </Card>

        {/* SECTION 3b: General wellness symptoms — multi-select chip
            tracker for common, everyday symptoms. NEVER diagnostic.
            "None" is treated as exclusive on the client (picking it
            clears the others, and picking any symptom clears "None") to
            keep tester data clean — there is no meaningful interpretation
            of "Headache + None". The educational guidance below stays
            visible regardless of selection. The supportive variant is
            shown when any non-"None" symptom is picked, gently pointing
            toward partnering with a healthcare professional for
            persistent or severe symptoms. */}
        <Card className="border-2 border-violet-200 shadow-lg rounded-2xl" data-testid="symptoms-section">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.symptoms.title')}</CardTitle>
            <p className="text-sm text-gray-600">{t('checkin.symptoms.q')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Wellness symptoms">
              {SYMPTOM_OPTIONS.map((opt) => {
                const active = symptoms.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      setSymptoms((prev) => {
                        if (opt.value === 'none') {
                          // "None" is exclusive: tapping it clears others.
                          return prev.includes('none') ? [] : ['none'];
                        }
                        // Picking any other symptom drops "None".
                        const next = prev.filter((v) => v !== 'none');
                        return next.includes(opt.value)
                          ? next.filter((v) => v !== opt.value)
                          : [...next, opt.value];
                      });
                    }}
                    data-testid={`symptom-${opt.value}`}
                    className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-all active:scale-[0.97] ${
                      active
                        ? 'border-violet-500 bg-violet-50 text-violet-900'
                        : 'border-gray-200 hover:border-violet-300 text-gray-700 bg-white'
                    }`}
                  >
                    {t(`checkin.symptoms.symptom_${opt.value}`)}
                  </button>
                );
              })}
            </div>

            {/* Educational guidance — always visible. The default copy
                stays calm and informational. When any non-"None" symptom
                is selected, we surface a slightly more direct supportive
                reminder about partnering with a healthcare professional. */}
            <div
              className="rounded-lg bg-violet-50/60 border border-violet-100 px-3 py-2.5 text-xs text-violet-900 leading-relaxed"
              data-testid="symptoms-nudge"
            >
              {symptoms.length > 0 && !symptoms.includes('none')
                ? t('checkin.symptoms.nudgeSupportive')
                : t('checkin.symptoms.nudge')}
            </div>
          </CardContent>
        </Card>

        {/* LIFESTYLE HABITS TODAY
            ----------------------------------------------------------------
            Observational, multi-select tracking of common daily lifestyle
            inputs that may correlate with sleep / energy / mood patterns
            over time. Uses the same exclusive-"None" chip pattern as the
            Symptoms section. Strictly educational — never diagnostic. */}
        <Card className="border-2 border-teal-200 shadow-lg rounded-2xl" data-testid="lifestyle-section">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.lifestyle.title')}</CardTitle>
            <p className="text-sm text-gray-600">{t('checkin.lifestyle.q')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Lifestyle habits today">
              {LIFESTYLE_OPTIONS.map((opt) => {
                const active = lifestyleHabits.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      setLifestyleHabits((prev) => {
                        if (opt.value === 'none') {
                          // "None of the above" is exclusive: tapping it
                          // clears all other selections.
                          return prev.includes('none') ? [] : ['none'];
                        }
                        // Picking any specific habit drops "None".
                        const next = prev.filter((v) => v !== 'none');
                        return next.includes(opt.value)
                          ? next.filter((v) => v !== opt.value)
                          : [...next, opt.value];
                      });
                    }}
                    data-testid={`lifestyle-${opt.value}`}
                    className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium transition-all active:scale-[0.97] ${
                      active
                        ? 'border-teal-500 bg-teal-50 text-teal-900'
                        : 'border-gray-200 hover:border-teal-300 text-gray-700 bg-white'
                    }`}
                  >
                    {t(`checkin.lifestyle.${opt.labelKey}`)}
                  </button>
                );
              })}
            </div>

            {/* Educational note — calm, non-judgemental tone. Always shown
                so users see WHY we ask, regardless of selection state. */}
            <div
              className="rounded-lg bg-teal-50/60 border border-teal-100 px-3 py-2.5 text-xs text-teal-900 leading-relaxed"
              data-testid="lifestyle-educational"
            >
              {t('checkin.lifestyle.educational')}
            </div>

            {/* Standard disclaimer — keeps the entire feature firmly in the
                "personal awareness" frame, never clinical. */}
            <p className="text-[11px] text-gray-500 italic" data-testid="lifestyle-disclaimer">
              {t('checkin.lifestyle.disclaimer')}
            </p>
          </CardContent>
        </Card>

        {/* SECTION 4: Your day */}
        <Card className="border-2 border-amber-200 shadow-lg rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.day.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium text-gray-900 mb-3">{t('checkin.day.routineQ')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setRoutine('yes')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  routine === 'yes' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <span className="font-medium">{t('checkin.day.yes')}</span>
              </button>
              <button
                onClick={() => setRoutine('no')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  routine === 'no' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <span className="font-medium">{t('checkin.day.no')}</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 5: Your environment */}
        <Card className="border-2 border-teal-200 shadow-lg rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg">{t('checkin.environment.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            
            {/* Environment description */}
            <div>
              <p className="font-medium text-gray-900 mb-3">{t('checkin.environment.describeQ')}</p>
              <div className="space-y-2">
                <button
                  onClick={() => setEnvironment('comfortable')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    environment === 'comfortable' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.environment.comfortable')}</span>
                </button>
                <button
                  onClick={() => setEnvironment('slightly_uncomfortable')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    environment === 'slightly_uncomfortable' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.environment.slightlyUncomfortable')}</span>
                </button>
                <button
                  onClick={() => setEnvironment('challenging')}
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    environment === 'challenging' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.environment.challenging')}</span>
                </button>
              </div>
            </div>

            {/* Environment affected */}
            <div>
              <p className="font-medium text-gray-900 mb-3">{t('checkin.environment.affectedQ')}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setEnvironmentAffected('yes')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    environmentAffected === 'yes' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.environment.yes')}</span>
                </button>
                <button
                  onClick={() => setEnvironmentAffected('no')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    environmentAffected === 'no' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <span className="font-medium">{t('checkin.environment.no')}</span>
                </button>
              </div>
            </div>

            {/* Exposures */}
            <div>
              <p className="font-medium text-gray-900 mb-3">{t('checkin.environment.exposureLabel')}</p>
              <div className="space-y-2">
                {[
                  { id: 'heat',       labelKey: 'checkin.environment.heat' },
                  { id: 'cold',       labelKey: 'checkin.environment.cold' },
                  { id: 'noise',      labelKey: 'checkin.environment.noise' },
                  { id: 'dust_smoke', labelKey: 'checkin.environment.dustSmoke' },
                  { id: 'crowded',    labelKey: 'checkin.environment.crowded' },
                  { id: 'none',       labelKey: 'checkin.environment.none' },
                ].map(exposure => (
                  <button
                    key={exposure.id}
                    onClick={() => toggleExposure(exposure.id)}
                    className={`w-full p-3 rounded-xl border-2 transition-all ${
                      exposures.includes(exposure.id)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {exposures.includes(exposure.id) ? (
                        <CheckCircle2 className="h-5 w-5 text-teal-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-400" />
                      )}
                      <span className="font-medium">{t(exposure.labelKey)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-lg font-bold rounded-2xl shadow-xl transition-all active:scale-95"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('checkin.submit.analyzing')}
              </span>
            ) : (
              t('checkin.submit.cta')
            )}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default SmartDailyCheckIn;
;
